<template>
  <parent>
    <div class="captain-organization d-flex justify-content-center">
      <transition
        name="component-fade"
        mode="out-in"
      >
        <template v-if="loading">
          <loading-page />
        </template>
        <template v-else>
          <div
            class="choose-organization"
            :class="{ 'm-h-100': totalCount > 5 }"
          >
            <organization-header :has-organization="hasOrganizations" />
            <div class="mb-1 col-12 col-sm-6 col-lg-4 col-xl-3">
              <b-button
                v-if="$can('create', 'organizations', 'captain')"
                variant="bg-primary"
                class="w-100 mb-2 mt-2"
                @click="createOrganization()"
              >
                <GjIcon
                  class="icon-left"
                  name="Plus"
                />
                Create Organization
              </b-button>
              <template v-if="hasOrganizations">
                <seperator />
                <div class="position-relative">
                  <template v-for="organization in organizations">
                    <div
                      :key="organization.id"
                      class="organization-button d-sm-flex mb-1 align-items-center"
                    >
                      <b-button
                        variant="bg-white"
                        class="w-100 text-normal button-shadow "
                        @click="setOrganization(organization.id)"
                      >
                        <span class="rounder-image icon-left" />
                        {{ organization.name }}
                        <GjIcon
                          class="ml-auto"
                          name="ArrowRight"
                        />
                      </b-button>
                      <template v-if="!fetchingUser">
                        <template
                          v-if="
                            updatingOrganization && orgId === organization.id
                          "
                        >
                          <span
                            class="default"
                          >Setting as default <b-spinner
                            class="spinner"
                          /></span>
                        </template>
                        <template v-else-if="orgId == organization.id">
                          <span class="default">
                            <a
                              v-b-popover.hover.focus="
                                'This organization is set as your default organization.'
                              "
                            ><GjIcon
                              name="Check"
                            /></a>
                            <a
                              class="text-primary edit"
                              @click="
                                updateDefaultProject(
                                  noDefaultOrganization ? false : true,
                                )
                              "
                            >{{
                              noDefaultOrganization ? 'Cancel' : 'Change'
                            }}</a>
                          </span>
                        </template>
                        <template v-else-if="noDefaultOrganization">
                          <a
                            class="default text-primary"
                            @click="setDefault(organization.id)"
                          >
                            Set as default
                          </a>
                        </template>
                      </template>
                    </div>
                  </template>
                  <table-busy
                    v-if="fetchingOrganizations"
                    style="top: 0; height: 100%;"
                  />
                </div>
                <div
                  v-if="totalCount > pageSize"
                  class="default-pagination flex-column mt-2"
                >
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalCount"
                    :per-page="pageSize"
                    first-number
                    last-number
                  />
                </div>
              </template>
            </div>
          </div>
        </template>
      </transition>
    </div>
  </parent>
</template>

<script>
import Seperator from '@/components/seperator/Seperator.vue'
import redirectError from '@/services/redirectError'
import OrganizationHeader from './parts/Header.vue'

export default {
  name: 'Organizations',
  components: {
    Seperator,
    OrganizationHeader,
  },
  data() {
    return {
      loading: true,
      orgId: null,
    }
  },
  computed: {
    organizations() {
      return this.$store.state.organization.organizations
    },
    hasOrganizations() {
      return this.organizations.length !== 0
    },
    fetchingOrganizations() {
      return this.$store.state.organization.fetchingOrganizations
    },
    resultInfo() {
      return this.$store.state.organization.organizationsResultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 5
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    organizationStatusOptions() {
      return this.$store.state.organization.organization
        .organizationStatusOptions
    },
    gettingOrganization() {
      return this.$store.state.organization.gettingOrganization
    },
    noDefaultOrganization() {
      return this.$store.state.users.noDefaultOrganization
    },
    user() {
      return this.$store.state.users.user
    },
    updatingOrganization() {
      return this.$store.state.users.updatingUser
    },
    fetchingUser() {
      return this.$store.state.users.fetchingUser
    },
    currentPage: {
      get() {
        return this.$store.getters['organization/organizationsCurrentPage']
      },
      set(val) {
        this.$store.commit('organization/SET_ORGANIZATIONS_CURRENT_PAGE', val)
      },
    },
  },
  watch: {
    currentPage() {
      this.fetchOrganizations()
    },
  },
  created() {
    if (this.$route.params.updateState) {
      this.$store.commit('RESET_STATE')
    }
    this.$store.commit('app/SET_ORGANIZATION_ID', null)
    this.$store.commit('app/SET_PROJECT_ID', null)

    this.$store
      .dispatch(
        'organization/getOrganization',
        '00000000-0000-0000-0000-000000000000',
      )
      .catch(error => {
        if (error.response) {
          const { status } = error.response
          if (status >= 500) {
            redirectError(error)
          }
          this.errorToast(error.response?.data?.errors[0])
        }
      })

    this.fetchOrganizations()

    this.$store.dispatch('users/fetchUser').then(() => {
      this.orgId = this.user.defaultOrganizationId
    })
  },
  methods: {
    setOrganization(id) {
      const org = this.organizations.find(o => o.id === id)

      if (org.status === 1) {
        this.$store.commit('app/SET_ORGANIZATION_ID', id)
        this.$router.push({ name: 'project', params: { organizationId: id } })
      } else {
        this.$router.push({
          name: 'edit-billing',
          params: { organizationId: id },
        })
      }
    },
    fetchOrganizations() {
      this.$store
        .dispatch('organization/fetchOrganizations', {
          pageSize: this.pageSize,
          pageIndex: this.pageIndex,
        })
        .catch(error => {
          if (error.response) {
            const { status } = error.response
            if (status >= 500) {
              redirectError(error)
              return
            }
            this.errorToast(
              error.response?.data?.errors[0]
                || 'Something went wrong while fetching organizations.',
            )
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    setDefault(id) {
      this.orgId = id
      this.$store
        .dispatch('users/updateUser', {
          defaultOrganizationId: this.orgId,
          defaultProjectId: null,
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating user details.')
        })
    },

    updateDefaultProject(val) {
      this.$store.commit('users/SET_NO_DEFAULT_ORGANIZATION', val)
    },

    createOrganization() {
      this.$router.push({ name: 'organization-add', query: this.$route.query })
    },
  },
}
</script>

<style lang="scss" scoped>
.captain-organization {
  height: 100%;
  align-items: center;

  .organization-button {
    position: relative;
    .default {
      @media (min-width: 576px) {
        position: absolute;
        left: 100%;
        width: 140px;
        margin-left: 15px;
      }

      @media (max-width: 575.8px) {
        margin-top: 8px;
      }
      .spinner {
        width: 1rem;
        height: 1rem;
        margin-left: 3px;
      }
      .edit {
        margin-left: 7px;
      }
    }
  }

  .choose-organization {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    &.m-h-100 {
      min-height: 100%;
    }
  }
}
</style>
